import { useCurrentUser } from '@mp/hooks';
import React, { useEffect, useRef } from 'react';
import { useApplicationMutations } from '../../applicationFlowMutations';
import calendlyWidget from '@mp/utils/calendlyWidget';
import { config } from '@mp/config';
import { LoadingState } from '../../components/LoadingState';

type CalendlyParams = {
  name?: string;
  email?: string;
  a1?: string;
};

const schedulingCompleted = (e: MessageEvent) =>
  e.origin === 'https://calendly.com' && e.data.event === 'calendly.event_scheduled';

export const PhoneScreeningScheduler = () => {
  const { currentUser, isLoading } = useCurrentUser();
  const { nextStepWithValues } = useApplicationMutations();
  const isPreScreening = currentUser?.professional?.application_version === 'prescreening';
  const calendlyURL = currentUser?.professional?.signup_assignment_id
    ? config.calendlyAssignmentURL
    : config.calendlyURL(isPreScreening);
  const processedEventRef = useRef(null);

  const buildCalendlyURL = (): string => {
    if (!currentUser) {
      return calendlyURL;
    }

    const { first_name, last_name, email, phone_number } = currentUser;
    const fullName = [first_name, last_name].filter(Boolean).join(' ').trim();
    const calendlyParams: CalendlyParams = {
      name: fullName,
      email,
      a1: phone_number,
    };

    const params = new URLSearchParams();

    Object.entries(calendlyParams).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    const queryString = params.toString();
    return queryString ? `${calendlyURL}?${queryString}` : calendlyURL;
  };

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    calendlyWidget();
  }, [currentUser]);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (schedulingCompleted(e)) {
        if (processedEventRef.current === e.data.payload.event.uri) {
          return;
        }
        processedEventRef.current = e.data.payload.event.uri;
        const payload = {
          calendly_scheduled_event_uri: e.data.payload.event.uri,
          calendly_scheduled_event_invitee_uri: e.data.payload.invitee.uri,
        };
        nextStepWithValues(payload);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [nextStepWithValues]);

  if (isLoading || !currentUser) {
    return <LoadingState />;
  }

  return (
    <div
      className="calendly-inline-widget"
      data-url={buildCalendlyURL()}
      style={{ minWidth: 320, height: 700 }}
    />
  );
};
